import * as yup from 'yup';
import { AlertTriangle, Check, Download, Info, Mail } from 'react-feather';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-form-state-manager';
import { useTranslation } from 'react-i18next';
import sortBy from 'lodash/sortBy';
import union from 'lodash/union';

import {
  ParticipantFieldImportErrorType as ErrorType,
  GetParticipantFieldImportDetailsQuery as GetParticipantFieldImportDetails,
} from '__generated__/graphql';
import { Identifier } from './ParticipantFieldImportExample';
import { getServerErrors } from '../../../common/helpers';
import { useNotifier } from '../../../common/Notifications/NotificationProvider';
import FinalizeParticipantFieldImportMutation from './FinalizeParticipantFieldImportMutation';
import UI from '../../../common/UI';
import useLocale from '../../../common/useLocale';
import useMutation from '../../../api/useMutation';
import useSendImportConfirmationMailModal from './useSendImportConfirmationMailModal';
import useValidation from '../../../common/useValidation';

type ParticipantFieldImport = GetParticipantFieldImportDetails['project']['participant_field_import'];
type DefaultExportColumn = GetParticipantFieldImportDetails['project']['default_export_columns'][0];
type ParticipantFieldImportError = GetParticipantFieldImportDetails['project']['participant_field_import']['errors'][0];

const finalizeParticipantFieldImportSchema = yup.object({
  overwrite: yup.bool().required(),
});

export interface FinalizeParticipantFieldImportFormProps {
  participantFieldImport: ParticipantFieldImport;
  defaultExportColumns: DefaultExportColumn[];
  onSuccess?: () => void;
  onClose?: () => void;
}

const FinalizeParticipantFieldImportForm = ({
  participantFieldImport, defaultExportColumns, onSuccess, onClose,
}: FinalizeParticipantFieldImportFormProps) => {
  const { t } = useTranslation();
  const { formatNumber } = useLocale();
  const notifier = useNotifier();

  const totalCount = participantFieldImport.length;
  const errorsCount = participantFieldImport.errors_count;
  const existingCount = participantFieldImport.existing_rows_count;

  const form = useForm({
    values: {
      overwrite: false,
    },
  });

  const importLength = totalCount - errorsCount - (!form.values.overwrite ? existingCount : 0);

  const rejected = participantFieldImport.validated_at && !participantFieldImport.ready_to_import
    && !participantFieldImport.importing && !participantFieldImport.imported_at;

  const [
    finalize, { loading, error },
  ] = useMutation(
    FinalizeParticipantFieldImportMutation,
    {
      variables: {
        input: {
          import: {
            id: participantFieldImport.id,
          },
          overwrite: form.values.overwrite,
        },
      },
      onCompleted: () => {
        onSuccess?.();
      },
    },
  );

  const importedRef = useRef(!!participantFieldImport.imported_at);

  useEffect(() => {
    if (!importedRef.current && participantFieldImport.imported_at) {
      notifier.success(t('notifications.import_complete'));
    }

    importedRef.current = !!participantFieldImport.imported_at;
  }, [participantFieldImport, notifier, t]);

  const [requireConfirm, setRequireConfirm] = useState(false);

  const handleSubmit = () => {
    if (!requireConfirm) {
      setRequireConfirm(true);
    } else {
      finalize();
      notifier.success(t('notifications.import_started'));
    }
  };

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setRequireConfirm(false);
    }, 10000);

    return () => window.clearTimeout(timer);
  }, [requireConfirm, setRequireConfirm]);

  const { errors, valid } = useValidation({
    schema: finalizeParticipantFieldImportSchema,
    values: form.values,
    externalErrors: getServerErrors(error),
  });

  const [openSendImportConfirmationModal] = useSendImportConfirmationMailModal({
    participantFieldImport,
  });

  const rows = [
    ...participantFieldImport.errors,
    ...participantFieldImport.parsed,
  ].slice(0, 1000);

  const [page, setPage] = useState(1);
  const perPage = 10;
  const lastPage = Math.ceil(rows.length / perPage);
  const previewRows = rows.slice((page - 1) * perPage, page * perPage);

  const getColumnStyle = (column: string, error: ParticipantFieldImportError) => {
    const errorStyle = {
      background: 'error.50',
      color: 'error.600',
    };

    return error.errors.filter((error) => error.column === column).length > 0 ? errorStyle : undefined;
  };

  const columnTitles = defaultExportColumns.reduce((acc, column) => {
    acc[column.id] = column.title;

    return acc;
  }, {} as Record<string, string>);

  const getProblems = (error: ParticipantFieldImportError) => {
    // Display the columns in the order they are in the import
    // Make sure to include the 'Invalid ID' error when the ID column is missing.
    const columns = union(participantFieldImport.columns, [participantFieldImport.unique_column]);

    const grouped = columns.reduce((acc, column) => {
      const errors = error.errors.filter((error) => error.column === column)[0]?.errors || [];

      errors.forEach((error) => {
        acc[error] = acc[error] || [];

        if (!acc[error].includes(column)) {
          acc[error].push(column);
        }
      });

      return acc;
    }, {} as Record<ErrorType, string[]>);

    return sortBy(Object.entries(grouped), ([error]) => error).map(([error, columns]) => {
      const titles = columns.map((column) => columnTitles[column]).filter((title) => !!title);

      return `${t(`participant_field_import_errors.${error}`)}${titles.length > 0 ? ` (${titles.join(', ')})` : ''}`;
    }).join(', ');
  };

  return (
    <UI.Form onSubmit={handleSubmit}>
      <UI.FormGrid>
        <UI.Div>
          <UI.H4>
            {t('review_import')}
          </UI.H4>
          <UI.InputDescription>
            {t('review_import_description')}
          </UI.InputDescription>
        </UI.Div>

        {rejected && (
          <UI.Error>
            {t('import_rejected')}
          </UI.Error>
        )}

        {participantFieldImport.imported_at && (
          <UI.Success>
            {t('import_already_finalized')}
          </UI.Success>
        )}

        {participantFieldImport.importing && (
          <UI.Info>
            {t('import_in_progress')}
          </UI.Info>
        )}

        {!participantFieldImport.validated_at && (
          <UI.Info>
            {t('import_validation_in_progress')}
          </UI.Info>
        )}

        <UI.ServerErrors error={error} />

        {previewRows.length > 0 && (
          <UI.FormGrid>
            <UI.Div>
              <UI.InputLabel>
                {t('preview')}
              </UI.InputLabel>
              <UI.InputDescription>
                {errorsCount > 0 && (
                  <>
                    {t('import_problems_description')}
                    {' '}
                    {!rejected && t('import_problems_alternative_description')}
                  </>
                )}
                {errorsCount === 0 && t('import_no_problems_description')}
              </UI.InputDescription>
            </UI.Div>
            <UI.Div style={{ maxWidth: '100%', overflowX: 'auto', boxShadow: 'inset -3px 0 5px rgba(0, 0, 0, 0.1)' }}>
              <UI.Table sc={{ bordered: true, noWrap: true }}>
                <UI.THead>
                  <UI.TR>
                    <UI.TH sc={{ background: 'gray.100', color: 'gray.500' }} style={{ width: 0 }}>
                      {t('row')}
                    </UI.TH>
                    <UI.TH sc={{ background: 'gray.100', color: 'gray.500' }} style={{ width: 0 }}>
                      {t('status')}
                    </UI.TH>
                    {participantFieldImport.field && (
                      <>
                        <UI.TH sc={{ background: 'gray.100', color: 'gray.500' }} style={{ width: 0 }}>
                          <Identifier field={participantFieldImport.field} />
                        </UI.TH>
                        <UI.TH
                          sc={{ background: 'gray.100', color: 'gray.500' }}
                          colSpan={previewRows[0].row.length - 1}
                        >
                          {t('value')}
                        </UI.TH>
                      </>
                    )}
                    {!participantFieldImport.field && participantFieldImport.columns.map((column) => (
                      <UI.TH
                        sc={{ background: 'gray.100', color: 'gray.500' }}
                        key={column}
                      >
                        {columnTitles[column]}
                      </UI.TH>
                    ))}
                    {errorsCount > 0 && (
                      <UI.TH sc={{ background: 'gray.100', color: 'gray.500' }}>
                        {t('problem')}
                      </UI.TH>
                    )}
                  </UI.TR>
                </UI.THead>
                <UI.TBody>
                  {previewRows.map((row) => (
                    <UI.TR key={row.index}>
                      <UI.TD
                        sc={{
                          background: 'gray.50',
                          color: 'gray.500',
                          textAlign: 'right',
                          small: true,
                        }}
                        style={{ verticalAlign: 'middle' }}
                      >
                        {row.index + 1}
                      </UI.TD>
                      <UI.TD sc={{ textAlign: 'center', color: row.errors.length > 0 ? 'error.500' : 'success' }}>
                        <UI.Icon>
                          {row.errors.length > 0 ? <AlertTriangle /> : <Check />}
                        </UI.Icon>
                      </UI.TD>
                      {row.row.map((value, index) => (
                        <UI.TD sc={getColumnStyle(participantFieldImport.columns[index], row)} key={index}>
                          {value}
                        </UI.TD>
                      ))}
                      {errorsCount > 0 && (
                        <UI.TD sc={{ background: 'gray.50', noWrap: true }}>
                          {getProblems(row)}
                        </UI.TD>
                      )}
                    </UI.TR>
                  ))}
                </UI.TBody>
              </UI.Table>
            </UI.Div>
            {rows.length > perPage && (
              <UI.Div>
                <UI.Button
                  onClick={() => setPage((page) => page - 1)}
                  disabled={page <= 1}
                  sc={{ size: 'sm' }}
                >
                  {t('previous')}
                </UI.Button>
                {' '}
                <UI.Button
                  onClick={() => setPage((page) => page + 1)}
                  disabled={page >= lastPage}
                  sc={{ size: 'sm', mr: 2 }}
                >
                  {t('next')}
                </UI.Button>
                {' '}
                <UI.Span sc={{ muted: true }}>
                  {t('page_x_out_of_y', { x: formatNumber(page), y: formatNumber(lastPage) })}
                </UI.Span>
                {rows.length < totalCount && (
                  <UI.Span sc={{ muted: true }}>
                    {' ('}
                    {t('only_showing_first_n_items', {
                      n: formatNumber(rows.length),
                    })}
                    )
                  </UI.Span>
                )}
              </UI.Div>
            )}
          </UI.FormGrid>
        )}

        {participantFieldImport.preview_url && (
          <UI.InputGroup>
            <UI.InputLabel>
              {t('download')}
            </UI.InputLabel>
            <UI.InputDescription sc={{ mb: 1 }}>
              {t('import_preview_description')}
            </UI.InputDescription>
            <UI.A
              href={participantFieldImport.preview_url}
              sc={{ disabled: !participantFieldImport.preview_url, fontWeight: 500, mr: 2 }}
              target="_blank"
              rel="noopener noreferrer"
            >
              <UI.Icon>
                <Download />
              </UI.Icon>
              {' '}
              {participantFieldImport.filename}
            </UI.A>
            <UI.Span sc={{ muted: true }}>
              {t('n_rows', { value: formatNumber(totalCount - errorsCount), count: totalCount - errorsCount })}
            </UI.Span>
          </UI.InputGroup>
        )}

        {participantFieldImport.ready_to_import && existingCount > 0 && (
          <UI.InputGroup sc={{ valid: !errors.overwrite, touched: form.touched.overwrite }}>
            <UI.InputLabel>
              <UI.Icon>
                <AlertTriangle />
              </UI.Icon>
              {' '}
              {t('existing_field_entries')}
              {' '}
              <UI.RequiredMark />
            </UI.InputLabel>
            <UI.InputDescription>
              {t('existing_field_entries_description', {
                count: existingCount,
                value: formatNumber(existingCount),
              })}
            </UI.InputDescription>
            <UI.Radio {...form.radio('overwrite', true)}>
              {t('overwrite_field_entries', {
                count: existingCount,
                value: formatNumber(existingCount),
              })}
            </UI.Radio>
            <UI.Radio {...form.radio('overwrite', false)}>
              {t('skip_field_entries')}
            </UI.Radio>
            <UI.ErrorMessages attribute={t('existing_field_entries')} errors={errors.overwrite} />
          </UI.InputGroup>
        )}

        {participantFieldImport.ready_to_import && !participantFieldImport.field && (
          <UI.Info icon={<Info />}>
            {t('import_confirmation_mail_info')}
          </UI.Info>
        )}

        {participantFieldImport.ready_to_import && (
          <>
            <UI.HR />
            <UI.FormButtons
              form={form}
              submitLabel={t('import_n_records', {
                count: importLength,
                n: formatNumber(importLength),
              })}
              loading={loading}
              valid={valid && importLength > 0 && !participantFieldImport.importing}
              onClose={onClose}
              enabled
              requireConfirm={requireConfirm}
            />
          </>
        )}

        {participantFieldImport.imported_at && participantFieldImport.confirmation_mail_recipients_count > 0 && (
          <UI.InputGroup>
            <UI.InputLabel>
              {t('confirmation_mails')}
            </UI.InputLabel>
            <UI.Button
              onClick={() => openSendImportConfirmationModal()}
              sc={{ mr: [2, 1] }}
              disabled={!!participantFieldImport.confirmation_mail_sent_at}
            >
              <UI.Icon sc={{ mr: 1.5 }}>
                {participantFieldImport.confirmation_mail_sending && <UI.Loader sc={{ brand: 'gray.700' }} />}
                {!participantFieldImport.confirmation_mail_sending && (
                  <>
                    {!participantFieldImport.confirmation_mail_sent_at && <Mail />}
                    {participantFieldImport.confirmation_mail_sent_at && <Check />}
                  </>
                )}
              </UI.Icon>
              {t('send_confirmation_mails')}
            </UI.Button>
          </UI.InputGroup>
        )}
      </UI.FormGrid>
    </UI.Form>
  );
};

export default FinalizeParticipantFieldImportForm;
